import { useState, useEffect, useRef } from 'react';
import AlbumItem from '../components/AlbumItem';

const AlbumGallery = ({ homePath, albumCode, albumItems, setSelectedItem }) => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const [scrollUp, setScrollUp] = useState(true);
    const [autoScroll, setAutoScroll] = useState(true);
    const [marginTop, setMarginTop] = useState(0);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const containerRef = useRef(null);
    const userScrollTimeoutRef = useRef(null);
    const touchStartRef = useRef(null);
  
    // Handle all forms of user scrolling (wheel, touch, keyboard)
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        
        const handleUserScroll = () => {
            setIsUserScrolling(true);
            setScrollPosition(container.scrollTop);
            
            // Clear any existing timeout
            if (userScrollTimeoutRef.current) {
                clearTimeout(userScrollTimeoutRef.current);
            }
            
            // Reset to auto-scrolling after 3 seconds of no user scrolling
            userScrollTimeoutRef.current = setTimeout(() => {
                setIsUserScrolling(false);
            }, 3000);
        };
        
        // Handle mouse wheel
        const handleWheel = () => handleUserScroll();
        
        // Handle touch start
        const handleTouchStart = (e) => {
        touchStartRef.current = e.touches[0].clientY;
        };
        
        // Handle touch move
        const handleTouchMove = () => handleUserScroll();
        
        // Handle keyboard navigation
        const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || 
            e.key === 'PageUp' || e.key === 'PageDown' || 
            e.key === 'Home' || e.key === 'End' || e.key === ' ') {
            handleUserScroll();
        }
        };
        
        // Handle normal scroll events (covers all input methods)
        const handleScroll = () => {
            if (isUserScrolling) {
                setScrollPosition(container.scrollTop);
            }
        };
        
        // Add all event listeners
        container.addEventListener('wheel', handleWheel, { passive: true });
        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: true });
        container.addEventListener('keydown', handleKeyDown);
        container.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            // Remove all event listeners
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('keydown', handleKeyDown);
            container.removeEventListener('scroll', handleScroll);
      
            if (userScrollTimeoutRef.current) {
                clearTimeout(userScrollTimeoutRef.current);
            }
        };
    }, [isUserScrolling]);
  
    useEffect(() => {
        setScrollPosition(0);
        setMarginTop(0);
    }, [albumItems]);
  
    useEffect(() => {
        if (!autoScroll) return;

        const container = containerRef.current;
        if (!container || isUserScrolling) return;

        // Calculate the total scroll height
        let scrollHeight = container.scrollHeight - container.clientHeight;
        // console.log(container.scrollHeight, container.clientHeight)
        // console.log(scrollHeight)

        // do not scroll if 2 or less albumItems
        // if ((scrollHeight > 0) && ((scrollHeight * 4) <= container.clientHeight)) return;
        // if (albumItems.length <= 2) return;

        // Define scrolling speed (lower is slower)
        const scrollSpeed = 1; // pixels per frame
        
        let animationFrameId;
        let lastTimestamp = null;
        
        const scrollStep = (timestamp) => {
            if (!lastTimestamp) lastTimestamp = timestamp;
            const elapsed = timestamp - lastTimestamp;
            
            // Only update every 16ms for smooth 60fps scrolling
            if (elapsed > 16) {
                lastTimestamp = timestamp;

                if (albumItems.length <= 2) {
                    const marginBottom = container.clientHeight - (222 * albumItems.length);
                    // console.log(container.clientHeight, marginBottom);

                    setMarginTop(prevPosition => {
                        const newPosition = prevPosition + (scrollUp ? scrollSpeed : (0-scrollSpeed));
                        
                        // If we've reached the bottom, reset to top
                        if (newPosition >= marginBottom) {
                            setScrollUp(false);
                            // console.log(newPosition);
                        }
                        if (newPosition <= 0) {
                            setScrollUp(true);
                        }
                        return newPosition;
                    });

                } else {
                    // Update scroll position
                    setScrollPosition(prevPosition => {
                        const newPosition = prevPosition + (scrollUp ? scrollSpeed : (0-scrollSpeed));
                        
                        // If we've reached the bottom, reset to top
                        if (newPosition >= scrollHeight) {
                            setScrollUp(false);
                        }
                        if (newPosition <= 0) {
                            setScrollUp(true);
                        }
                        // console.log(newPosition);
                        return newPosition;
                    });
                    
                    // Apply the scroll position
                    // console.log(scrollPosition)
                    container.scrollTop = scrollPosition;
                }              
            }
            
            animationFrameId = requestAnimationFrame(scrollStep);
        };
        
        animationFrameId = requestAnimationFrame(scrollStep);
        
        return () => {
            // console.log("cancel")
            cancelAnimationFrame(animationFrameId);
        };
    }, [scrollPosition, autoScroll, isUserScrolling, albumItems, marginTop]);
  
    useEffect(() => {
        setTimeout(() => {
            if (albumItems.length > 0) {
                handleClick(albumItems[0]);
            }
        }, 1000);
    }, []);

    const handleClick = (param, e) => {
        // console.log(param);
        setSelectedItem(param);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }

    const countText = (count) => {
        if (count < 1000) return count;
        if (count < 1000000) return (count / 1000).toFixed(1) + 'K';
        if (count < 1000000000) return (count / 1000000).toFixed(1) + 'M';
        if (count < 1000000000000) return (count / 1000000000).toFixed(1) + 'B';
        return count;
    };

    const divClass = 'h-[calc(88vh)] overscroll-contain overflow-x-hidden touch-pan-y ml-2'

    return (
        <div className="bg-transparent rounded-lg min-h-[calc(90vh)] w-75 mt-2">
            <div 
                ref={containerRef}
                // container.scrollHeight depends on number of albumItems
                // container.clientHeight = 121 x 4 = 484 + 16 + 16 = 516 / 4 = 129
                // container.clientWidth  = 384 / 4 = 96
                className={`${divClass} overflow-hidden`}
                onMouseEnter={() => {
                    setAutoScroll(false);
                    setIsUserScrolling(true);
                    containerRef.current.className = `${divClass} overflow-y-auto`
                }}
                onMouseLeave={() => {
                    setAutoScroll(true);
                    setIsUserScrolling(false);
                    containerRef.current.className = `${divClass} overflow-y-hidden`
                }}
            >
                <div className=""
                    style={{
                        marginTop: `${marginTop}px`
                    }}
                >
                    {albumItems.map((item, index) => (
                        <div key={item.id}
                            className="flex-1 items-center justify-center overflow-hidden transition-all duration-300 hover:scale-103"
                            onClick={handleClick.bind(null, index)}
                        >
                            <AlbumItem
                                homePath={homePath}
                                albumCode={albumCode}
                                fileName={albumItems[index].filename}
                            />                  
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AlbumGallery;

